import { ComponentProps, forwardRef, LegacyRef } from 'react';
import { css } from '@emotion/react';
import { PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { useMutation } from 'react-query';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PatientPortalApi } from '@frontend/api-patient-portal';
import { PersonHelpers } from '@frontend/api-person';
import { PetListAction, useSendPetReport } from '@frontend/contact-actions-service-report';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useGetActivePetQuery, useGetActivePetsQuery } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, SpinningLoader, useAlert, usePopoverMenu } from '@frontend/design-system';
import { ContactDetails } from '../../contact-details/contact-details';
import { ContactDetailsHeader } from '../../contact-details/contact-details-header';
import { CustomerDetailsProps } from '../customer-details';
import { PetList } from './pet-list';

export const PetOverview = ({ data, locationId }: CustomerDetailsProps) => {
  const tokens = useTranslationTokens();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasPetServiceReport = getFeatureFlagValue('pet-service-report');
  const petsQuery = useGetActivePetsQuery({
    locationId: locationId || '',
    personId: data?.PersonID || '',
  });
  const petsData = petsQuery.data || [];

  const { openModal, SendInThreadModal, disabled, disabledText } = useSendPetReport({
    locationId: locationId || '',
    mobilePhone: data?.MobilePhone || '',
    personId: data?.PersonID || '',
    petOwnerName: PersonHelpers.getFullName({ FirstName: data?.FirstName, LastName: data?.LastName }),
  });
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({
    placement: 'left-start',
    middlewareOptions: { offset: 0 },
  });

  const magicLinkFeatureFlag = getFeatureFlagValue('generate-patient-portal-magic-link');

  if (petsQuery.isLoading) {
    return (
      <ContactDetails>
        <section
          css={css`
            padding-bottom: ${theme.spacing(2)};
          `}
        >
          <ContactDetailsHeader title={tokens.household} css={{ padding: theme.spacing(2, 2, 0, 2) }} />
          <SpinningLoader size='medium' css={{ margin: 'auto', display: 'flex', justifyContent: 'center' }} />
        </section>
      </ContactDetails>
    );
  }

  if (!petsData?.length) return null;

  return (
    <ContactDetails>
      <section css={{ paddingBottom: theme.spacing(2) }}>
        {hasPetServiceReport ? (
          <>
            <ContactDetailsHeader
              title={tokens.household}
              css={{ padding: theme.spacing(2, 2, 0, 2) }}
              customAction={
                petsData?.length === 1 ? (
                  <SinglePetAction
                    pet={petsData[0]}
                    openModal={openModal}
                    locationId={locationId || ''}
                    disabled={disabled}
                    hoverLabel={disabledText}
                    showLabelWhenDisabled
                  />
                ) : (
                  <CreateServiceReportButton
                    {...getTriggerProps()}
                    disabled={disabled}
                    hoverLabel={disabledText}
                    showLabelWhenDisabled
                  />
                )
              }
            />
            {SendInThreadModal}
            <PopoverMenu {...getMenuProps()}>
              {petsData?.map((pet, index) => (
                <PetListAction
                  key={pet.petId}
                  pet={pet}
                  index={index}
                  getItemProps={getItemProps}
                  trackingId='contact-panel-waterfall-send-vaccine-history'
                  handleClick={openModal}
                  locationId={locationId}
                />
              ))}
            </PopoverMenu>
          </>
        ) : (
          <ContactDetailsHeader title={tokens.household} css={{ padding: theme.spacing(2, 2, 0, 2) }} />
        )}
        {magicLinkFeatureFlag && <GenerateMagicLink data={data} locationId={locationId} />}
        <PetList petsData={petsData} sourceId={data?.SourceID || ''} />
      </section>
    </ContactDetails>
  );
};

const GenerateMagicLink = ({ data, locationId }: CustomerDetailsProps) => {
  const alert = useAlert();
  const { t } = useTranslation('contacts');

  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');

  const generateMagicLinkMutation = useMutation({
    mutationFn: () =>
      PatientPortalApi.generatePortalMagicLink({
        personId: data?.PersonID ?? '',
        // if this gets put in front of customers, we will need to refine this logic
        locationId: locationId ?? demoSourceIds?.[0] ?? '',
      }),
    onSuccess: () => {
      alert.success(t(`Successfully sent portal login to ${PersonHelpers.getFullName(data)}.`));
    },
    onError: () => {
      alert.error(t(`Unable to send portal login to ${PersonHelpers.getFullName(data)}.`));
    },
  });

  return (
    <Button
      variant='secondary'
      css={{ margin: theme.spacing(1) }}
      onClick={() => {
        if (data?.Email === '') {
          alert.error(t('No email address to send to.'));
          return;
        } else {
          generateMagicLinkMutation.mutate();
        }
      }}
    >
      Generate Portal Link
    </Button>
  );
};

const SinglePetAction = ({
  pet,
  openModal,
  locationId,
  ...rest
}: {
  pet: PetDetails;
  locationId: string;
  openModal: ReturnType<typeof useSendPetReport>['openModal'];
} & Omit<ComponentProps<typeof CreateServiceReportButton>, 'onClick' | 'ref'>) => {
  const petDetailsQuery = useGetActivePetQuery({ petId: pet.petId, locationId });
  return (
    <CreateServiceReportButton
      onClick={() => {
        if (!petDetailsQuery.data) return;
        openModal(petDetailsQuery.data);
      }}
      {...rest}
    />
  );
};

const CreateServiceReportButton = forwardRef(
  (
    { onClick, ...rest }: Compulsory<Omit<ComponentProps<typeof Button>, 'ref' | 'children'>, 'onClick'>,
    ref: LegacyRef<HTMLButtonElement>
  ) => {
    const { t } = useTranslation();
    const { personLocationId } = useContactPanelShallowStore('personLocationId');
    const alerts = useAlert();
    return (
      <Button
        variant='tertiary'
        onClick={(e) => {
          if (!personLocationId) {
            alerts.info(t('No locationId selected.'));
            return;
          }
          onClick(e);
        }}
        ref={ref}
        css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}
        iconName='list-small'
        {...rest}
        trackingId='contact-panel-send-vaccine-history'
      >
        {t('Send Vaccine Report')}
      </Button>
    );
  }
);
CreateServiceReportButton.displayName = 'CreateServiceReportButton';
